import {
  CalendarClock,
  CheckCircle2,
  FolderOpen,
  HardHat,
  MapPin,
  Users,
  Wrench,
} from 'lucide-react';
import type { PortalAssemblyTask } from '@proerp/shared';
import { Badge } from '@proerp/ui';
import { EmptyNote, ErrorCard, LoadingNote, PageHeader } from '@/components/PageLayout';
import { ReportPreviewButton } from '@/components/ReportPreviewButton';
import { StatTile } from '@/components/StatTile';
import { TaskDayLogs } from '@/components/TaskDayLogs';
import { TaskDocuments } from '@/components/TaskDocuments';
import { assemblyStatusLabel, formatAssemblyPeriod, splitAssemblyTasks } from '@/lib/formatAssembly';
import { assemblyKpis } from '@/lib/portalMetrics';
import { usePortalAssembly } from '@/lib/usePortalAssembly';

/**
 * Montage-Einsätze im Partnerportal (ADR 0085, Stufe 2): die Einsätze, die
 * diesem Partner zugeordnet sind — was, wo, wann, wie ist der Stand.
 *
 * Als **Zeitstrahl** statt als Liste: Ein Monteur plant seine Wochen, und eine
 * durchgehende Achse mit Punkten zeigt Reihenfolge und Lücken auf einen Blick.
 * Der Stand steht trotzdem als Wort daneben — die Farbe des Punktes ist
 * Begleitung, nie die einzige Aussage (WCAG).
 *
 * Sprachlich gilt CLAUDE.md §12: **Monteur**, nie „Techniker" — letzteres
 * bezeichnet bei PROBIG das Büro-Personal in der Konstruktion.
 *
 * Seit ADR 0087 steht am Einsatz alles, was zum Arbeiten nötig ist: Projekt,
 * Art des Einsatzes, wer sonst vor Ort ist — und die **Anweisungen**. Die
 * stehen bewusst nicht als Kleingedrucktes am Rand, sondern als eigener Block:
 * Es ist die einzige Angabe, die man vor der Anfahrt gelesen haben muss.
 */
export function AssemblyPage() {
  const assembly = usePortalAssembly();

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Montage"
        title="Deine Einsätze"
        subtitle="Die Einsätze, für die wir dich eingeplant haben — mit Ort und Zeitraum."
        action={
          assembly.data?.tasks.length ? (
            <ReportPreviewButton
              path="/assembly/pdf"
              title="Montage-Einsätze"
              filename="Montage-Einsaetze.pdf"
            />
          ) : null
        }
      />

      {assembly.isPending && <LoadingNote />}

      {assembly.isError && (
        <ErrorCard
          title="Die Einsätze sind gerade nicht abrufbar."
          error={assembly.error}
          onRetry={() => void assembly.refetch()}
        />
      )}

      {assembly.data && <AssemblyBody tasks={assembly.data.tasks} />}
    </div>
  );
}

function AssemblyBody({ tasks }: { tasks: PortalAssemblyTask[] }) {
  if (tasks.length === 0) {
    return (
      <EmptyNote icon={<Wrench className="size-5" aria-hidden />}>
        Für dich ist derzeit kein Einsatz eingeplant. Sobald wir dich einplanen, findest du ihn hier.
      </EmptyNote>
    );
  }

  const { open, done } = splitAssemblyTasks(tasks);
  const kpis = assemblyKpis(tasks);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <StatTile
          label="Steht an"
          value={String(kpis.openCount)}
          hint={kpis.undatedCount > 0 ? `${kpis.undatedCount} ohne Termin` : 'alle terminiert'}
          icon={<HardHat className="size-4" aria-hidden />}
          tone={kpis.undatedCount > 0 ? 'warning' : 'accent'}
        />
        <StatTile
          label="Erledigt"
          value={String(kpis.doneCount)}
          hint="abgeschlossen oder abgesagt"
          icon={<CheckCircle2 className="size-4" aria-hidden />}
        />
      </div>

      {open.length > 0 && <TaskTimeline title="Steht an" tasks={open} />}
      {done.length > 0 && <TaskTimeline title="Erledigt" tasks={done} muted />}
    </div>
  );
}

/**
 * Projekt als eine Angabe: Nummer und Name, sofern beides da ist. Die Nummer
 * allein ist für uns eindeutig, für den Partner aber nur ein Code; der Name
 * allein lässt sich keiner Rückfrage zuordnen.
 */
function projectLabel(task: PortalAssemblyTask): string {
  return [task.projectCode, task.projectName].filter(Boolean).join(' · ');
}

/** Punktfarbe je Stand — begleitend, nie alleiniger Träger der Aussage. */
function dotClass(status: string, muted: boolean): string {
  if (status === 'CANCELLED') return 'bg-danger-500';
  if (status === 'CONFIRMED') return 'bg-[color:var(--chart-1)]';
  if (muted) return 'bg-muted-foreground/40';
  return 'bg-[color:var(--chart-1)]/50';
}

function TaskTimeline({
  title,
  tasks,
  muted = false,
}: {
  title: string;
  tasks: PortalAssemblyTask[];
  muted?: boolean;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
      {/* Die Schiene ist reine Dekoration und deshalb aus dem Baum genommen. */}
      <ol className="relative flex flex-col gap-3 before:absolute before:bottom-4 before:left-[7px] before:top-4 before:w-px before:bg-border before:content-['']">
        {tasks.map((task) => (
          <li key={task.id} className="relative pl-7">
            <span
              className={`absolute left-0 top-5 size-[15px] rounded-full ring-4 ring-background ${dotClass(task.status, muted)}`}
              aria-hidden
            />
            <div
              className={
                muted
                  ? 'rounded-xl border border-border bg-card/60 p-4 opacity-80'
                  : 'rounded-xl border border-border bg-card p-4 transition-colors hover:border-[color:var(--chart-1)]/40'
              }
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-medium text-foreground">{task.title}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {task.categoryName && <Badge tone="outline">{task.categoryName}</Badge>}
                  <Badge tone={task.status === 'CANCELLED' ? 'danger' : muted ? 'neutral' : 'info'}>
                    {assemblyStatusLabel(task.status)}
                  </Badge>
                </div>
              </div>
              <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <CalendarClock className="size-4" aria-hidden />
                  <dt className="sr-only">Zeitraum</dt>
                  <dd className="tabular-nums">
                    {formatAssemblyPeriod(task.plannedStart, task.plannedEnd)}
                  </dd>
                </div>
                {task.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="size-4" aria-hidden />
                    <dt className="sr-only">Einsatzort</dt>
                    <dd>{task.location}</dd>
                  </div>
                )}
                {projectLabel(task) && (
                  <div className="flex items-center gap-1.5">
                    <FolderOpen className="size-4" aria-hidden />
                    <dt className="sr-only">Projekt</dt>
                    <dd>{projectLabel(task)}</dd>
                  </div>
                )}
                {task.crew && (
                  <div className="flex items-center gap-1.5">
                    <Users className="size-4" aria-hidden />
                    <dt className="sr-only">Vor Ort</dt>
                    <dd>{task.crew}</dd>
                  </div>
                )}
              </dl>

              {/* Die Anweisungen sind der Grund, warum jemand diese Seite
                  aufmacht — deshalb abgesetzt und nicht in der Metazeile. */}
              {task.notes && (
                <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Anweisungen
                  </p>
                  <p className="mt-1 whitespace-pre-line text-sm text-foreground">{task.notes}</p>
                </div>
              )}

              <TaskDocuments taskId={task.id} />
              <TaskDayLogs taskId={task.id} location={task.location} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
